import React, { useRef, useState } from 'react';
import { CategoryBarData } from 'shared/types/product/Category';
import { useTranslations } from 'use-intl';
import Link from 'components/commercetools-ui/atoms/link';
import { AnnouncementBarDataType } from 'components/commercetools-ui/organisms/announcement-bar/types';
import { trackMenuNavigation } from 'helpers/analytics/ga4Events';
import useClassNames from 'helpers/hooks/useClassNames';
import { useFocusOutside } from 'helpers/hooks/useFocusOutside';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useOnClickOutside from 'helpers/hooks/useOnClickOutside';
import useSticky from 'helpers/hooks/useSticky';
import { desktop } from 'helpers/utils/screensizes';
import HeaderNavigationSlider from './header-navigation-slider';
import MegaMenu, { FOCUSABLE_SELECTOR } from './menu-dropdown/mega-menu';

export interface Props {
  links: CategoryBarData[];
  announcementBarData?: AnnouncementBarDataType;
}

const HeaderNavigationDesktop: React.FC<Props> = ({ links, announcementBarData }) => {
  const [activeCategory, setActiveCategory] = useState<CategoryBarData | undefined>();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [shouldFocusFirstMenuItem, setShouldFocusFirstMenuItem] = useState(false);
  const isKeyboardNavigationRef = useRef(false);
  const isRestoringFocusRef = useRef(false);

  const heroBannerHeight = useSticky(['heroBanner-ref']);
  const { render } = announcementBarData ?? {};
  const [isDesktopSize] = useMediaQuery(desktop);
  const translate = useTranslations();
  const pointerTypeRef = useRef<string>('mouse');

  const annouceBarHeight = isDesktopSize ? 40 : 28;
  const totalHeight = render ? annouceBarHeight + 48 : 48;

  const navRef = useRef<HTMLDivElement>(null);
  const megaMenuRef = useRef<HTMLDivElement>(null);
  const { ref } = useFocusOutside(() => setActiveCategory(undefined));
  useOnClickOutside(ref, () => setActiveCategory(undefined));

  const closeTimeout = useRef<number | null>(null);
  const OPEN_DELAY = 400;

  const openMenu = (link: CategoryBarData, el: HTMLElement, shouldFocusFirstItem = false) => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
    setShouldFocusFirstMenuItem(shouldFocusFirstItem);
    setActiveCategory(link);
    setAnchorEl(el);
  };

  const scheduleCloseMenu = () => {
    closeTimeout.current = window.setTimeout(() => {
      setActiveCategory(undefined);
      setAnchorEl(null);
    }, OPEN_DELAY);
  };

  const cancelCloseMenu = () => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
  };

  // offset 0 refocuses the current trigger, 1 moves to the next one
  const focusCategoryByOffset = (currentCategoryName: string | undefined, offset: number) => {
    const currentIndex = links.findIndex((link) => link.categoryName === currentCategoryName);
    if (currentIndex === -1) return false;

    const targetLink = navRef.current?.querySelector<HTMLElement>(`[data-cat-index="${currentIndex + offset}"]`);
    if (!targetLink) return false;

    targetLink.focus();
    return true;
  };

  const navigationClassNames = useClassNames([
    isDesktopSize ? 'flex items-center justify-center flex-col' : 'hidden',
    'fixed z-30 dark:bg-gmsb-header-gray',
  ]);

  console.info('Header : ', {
    categoryName: activeCategory?.categoryName,
    subcategoryOptions: activeCategory?.subcategoryOptions,
  });

  return (
    <div className="flex items-center justify-center">
      {links && (
        <div
          ref={(el) => {
            navRef.current = el;
            ref.current = el;
          }}
          onKeyDownCapture={() => {
            isKeyboardNavigationRef.current = true;
          }}
          onPointerDownCapture={() => {
            isKeyboardNavigationRef.current = false;
          }}
          className={navigationClassNames}
          style={{
            top: `${totalHeight}px`,
            position: 'fixed',
            marginTop: heroBannerHeight ? '-1.5px' : '-2px',
          }}
        >
          <nav>
            <a
              href="#main-content"
              className="sr-only focus:not-sr-only focus:absolute focus:left-12 focus:top-12 focus:z-[9999] focus:rounded-md focus:bg-black focus:px-24 focus:py-12 focus:text-white"
              data-ffid="lnk-skip-to-content"
            >
              {'Skip to main content'}
            </a>

            <div className="container mt-20 h-48 rounded-full border bg-white px-10 shadow">
              <div className="flex h-48 items-center justify-center gap-8 whitespace-nowrap">
                <HeaderNavigationSlider
                  a11y={{
                    enabled: true,
                  }}
                  allowTouchMove={false}
                  loop={false}
                  freeMode={false}
                  uniqueFFIDText="category-bar"
                  data-ffid="ctr-cat-menu-slider"
                >
                  {links.map((link, index) => {
                    const isActive = link.categoryName === activeCategory?.categoryName;

                    const linkUrl =
                      typeof link.categoryLinks === 'string' ? link.categoryLinks : (link.categoryLinks?.link ?? '');
                    const hasSubmenu = (link.subcategoryOptions?.length ?? 0) > 0;

                    return (
                      <Link
                        key={`${link?.categoryName}-${index}`}
                        link={link.categoryLinks}
                        className={`relative flex flex-col items-center justify-center px-20 ${isActive ? 'text-white' : 'text-[#5C5C5C]'
                          }`}
                        aria-haspopup="menu"
                        aria-expanded={isActive}
                        considerQueryParams
                        aria-label={
                          link.categoryName
                            ? `${translate('category.menu')} ${link.categoryName}`
                            : translate('category.menu')
                        }
                        data-ffid={`lnk-cat-menu-${link.categoryNameFFID}`}
                        data-cat-index={index}
                        onPointerEnter={(e) => {
                          if (e.pointerType === 'mouse') {
                            isKeyboardNavigationRef.current = false;
                            openMenu(link, e.currentTarget);
                          }
                        }}
                        onFocus={(e) => {
                          if (isRestoringFocusRef.current) {
                            isRestoringFocusRef.current = false;
                            return;
                          }

                          openMenu(
                            link,
                            e.currentTarget,
                            e.currentTarget.matches(':focus-visible') || isKeyboardNavigationRef.current,
                          );
                        }}
                        onPointerLeave={(e) => {
                          if (e.pointerType === 'mouse') scheduleCloseMenu();
                        }}
                        onPointerDown={(e) => {
                          pointerTypeRef.current = e.pointerType;
                        }}
                        onKeyDown={(e) => {
                          if (!isActive) return;

                          if (e.key === 'ArrowDown') {
                            e.preventDefault();

                            const firstFocusable =
                              megaMenuRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);

                            firstFocusable?.focus();
                          }
                        }}
                        onClick={(e) => {
                          trackMenuNavigation('Category Bar', link.categoryName ?? '', linkUrl);

                          if (pointerTypeRef.current === 'touch' && hasSubmenu) {
                            e.preventDefault();

                            if (isActive) {
                              setActiveCategory(undefined);
                              setAnchorEl(null);
                            } else {
                              openMenu(link, e.currentTarget);
                            }
                          }
                        }}
                      >
                        <span
                          className="relative z-[2] text-14"
                          data-ffid={`lbl-cat-menu-item-${link.categoryNameFFID}`}
                        >
                          {link.categoryName}
                        </span>

                        <div
                          data-ffid={`lin-hover-underline-${link.categoryNameFFID}`}
                          aria-hidden="true"
                          className={`absolute left-0 h-32 w-full rounded-full transition-all duration-300 ${isActive ? 'scale-x-100 bg-[#018463] opacity-100' : 'scale-x-75 opacity-0'
                            }`}
                        />
                      </Link>
                    );
                  })}
                </HeaderNavigationSlider>
              </div>
            </div>
          </nav>

          {(activeCategory?.subcategoryOptions?.length ?? 0) > 0 && (
            <MegaMenu
              ref={megaMenuRef}
              isOpen={!!activeCategory}
              shouldFocusFirstItem={shouldFocusFirstMenuItem}
              category={activeCategory}
              anchorEl={anchorEl}
              onClick={() => {
                setTimeout(() => {
                  setActiveCategory(undefined);
                }, 300);
              }}
              onMouseEnter={cancelCloseMenu}
              onMouseLeave={scheduleCloseMenu}
              onTabOutForward={() => focusCategoryByOffset(activeCategory?.categoryName, 1)}
              onTabOutBackward={() => focusCategoryByOffset(activeCategory?.categoryName, 0)}
              onEscape={() => {
                if (anchorEl && document.activeElement !== anchorEl) {
                  isRestoringFocusRef.current = true;
                  anchorEl.focus();
                }
                setActiveCategory(undefined);
                setAnchorEl(null);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default HeaderNavigationDesktop;
